import { memo } from 'react';

import { ChevronRightIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link } from 'react-router';

import { focusVisibleRing } from '@/components/ui/constants';
import { useDeskId } from '@/hooks/useDeskId';
import { cn } from '@/lib/utils';
import { selectAccountSlug } from '@/redux/session/selectors';
import { Icon as IconType } from '@/shared/types/icon';
import { IntegrationType } from '@/shared/types/integration';

interface IntegrationGroupItemProps {
  type: IntegrationType;
  name: string;
  Icon: React.FC<IconType>;
  count?: number;
}

/**
 * IntegrationGroupItem - Integration group component for multi-instance integrations
 *
 * Displays an integration type that can have multiple instances (e.g., multiple email addresses).
 * Navigates to a management/list page where users can view and manage all instances of this integration type.
 *
 * Features:
 * - Shows integration icon and name
 * - Displays count of configured instances (if provided)
 * - Navigates to integration type management page
 * - Respects write permissions for interaction
 *
 * @param {IntegrationGroupItemProps} props - Component props
 * @param {IntegrationType} props.type - The type of integration (e.g., 'email')
 * @param {string} props.name - Display name for the integration
 * @param {React.FC<IconType>} props.Icon - Icon component to display
 * @param {number} [props.count] - Number of configured instances (optional)
 * @param {string} [props.subtitle] - Custom subtitle text to display (optional)
 * @returns {JSX.Element} The integration group item component
 *
 * @example
 * ```tsx
 * <IntegrationGroupItem
 *   type="email"
 *   name="Email"
 *   Icon={EmailIcon}
 *   count={3}
 * />
 * ```
 */
const IntegrationGroupItem = memo(({ type, name, Icon, count }: IntegrationGroupItemProps) => {
  const { t } = useTranslation();
  const deskId = useDeskId();
  const slug = useSelector(selectAccountSlug);

  const hasInstances = count !== undefined && count > 0;

  const content = (
    <>
      {/* Icon */}
      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded border-[0.5px] border-stroke bg-bg-muted-light p-2">
        <div className="flex items-center justify-center rounded bg-background p-1 shadow-sm">
          <Icon className="h-6 w-6" />
        </div>
      </div>

      {/* Name and additional info */}
      <div className="flex min-w-0 flex-1 flex-col justify-center gap-1">
        <span className="text-base leading-none font-medium text-foreground">{name}</span>
        {hasInstances && (
          <span className="truncate text-sm leading-5 font-normal text-foreground-muted">
            {t('connections.configured_count', { 0: count })}
          </span>
        )}
      </div>

      <ChevronRightIcon
        size={24}
        className="shrink-0 text-foreground-muted opacity-0 transition-opacity group-hover:opacity-100"
      />
    </>
  );

  // Navigate to integration type management page. It should be defined in the Intagration router.
  const managementPath = `/${slug}/environments/${deskId}/integrations/${type}`;

  return (
    <Link
      to={managementPath}
      className={cn(
        'group flex w-full items-center gap-4 rounded-lg border border-stroke bg-background px-4 py-4 transition-colors',
        focusVisibleRing,
        'cursor-pointer hover:bg-bg-muted-50'
      )}
    >
      {content}
    </Link>
  );
});

IntegrationGroupItem.displayName = 'IntegrationGroupItem';

export default IntegrationGroupItem;
